import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import SalesAppHeader from "../components/SalesAppHeader";
import {
  searchOffers,
  saveOfferTracking,
  getOfferRead,
  createOffer,
} from "../api/salesClient";
import { decodeOfferId } from "../../lib/offer-key.mjs";
import { listAllUnits, listUnitsByProject } from "../services/unitsClient";

function formatPhone(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 11 && digits.startsWith("1")) {
    const local = digits.slice(1);
    return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 7) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return String(value || "").trim();
}

const DATE_FIELDS = [
  "contract_sent_date",
  "fully_executed_date",
  "week_ratified_date",
  "projected_closing_date",
  "initial_deposit_receipt_date",
  "financing_contingency_date",
  "loan_app_complete",
  "loan_approved",
  "loan_lock",
  "appraisal_ordered",
  "appraiser_visit_date",
  "appraisal_complete",
  "walk_through_date",
  "extended_adjusted_coe",
  "notice_to_close",
  "loan_docs_ordered",
  "loan_docs_signed",
  "loan_fund",
  "coe_date",
  "buyer_complete",
  "envelope_sent_date",
  "buyer_sign_date",
];

const DATE_FIELD_LABELS = {
  week_ratified_date: "ratified date",
  extended_adjusted_coe: "extended/adjusted coe",
};

const DATE_FIELDS_UI = DATE_FIELDS.filter((field) => field !== "fully_executed_date");

const NUMERIC_FIELDS = [
  "base_price",
  "final_price",
  "list_price",
  "initial_deposit_amount",
  "seller_credit",
  "upgrade_credit",
  "total_upgrades_solar",
  "hoa_credit",
  "total_credits",
];

const CREDIT_FIELDS = ["seller_credit", "upgrade_credit", "total_upgrades_solar", "hoa_credit"];
const NUMERIC_FIELDS_SET = new Set(NUMERIC_FIELDS);
const FIELD_ALIASES = {
  week_ratified_date: ["fully_executed_date"],
  fully_executed_date: ["week_ratified_date"],
  extended_adjusted_coe: ["adjusted_coe"],
};

const STATUS_NUMERIC_MAP = {
  "pending release": 5,
  pending: 5,
  available: 4,
  "contract sent": 3,
  contract_sent: 3,
  "ratified - fully executed": 2,
  ratified: 2,
  closed: 1,
};

function statusNumericFor(value) {
  const key = String(value || "").trim().toLowerCase();
  if (!key) return "";
  if (key === "canceled" || key === "cancelled") return "";
  return STATUS_NUMERIC_MAP[key] ?? "";
}

function formatDateForInput(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
}

function normalizeNumeric(value) {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  return Number.isFinite(num) ? num : "";
}

function todayInputDate() {
  const now = new Date()
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 10)
}

function applyStatusDerivedDates(target, statusValue) {
  const key = String(statusValue || target?.status || "").trim().toLowerCase();
  if (!key || !target) return target;

  if (key === "closed" && !target.coe_date) {
    target.coe_date = todayInputDate();
  }

  if (key === "ratified" || key === "ratified - fully executed" || key === "backlog") {
    const ratifiedDate =
      target.week_ratified_date || target.fully_executed_date || todayInputDate();
    if (!target.week_ratified_date) {
      target.week_ratified_date = ratifiedDate;
    }
    if (!target.fully_executed_date) {
      target.fully_executed_date = ratifiedDate;
    }
  }

  return target;
}

function calculateTotalCredits(data) {
  const anyFilled = CREDIT_FIELDS.some((key) => {
    const val = data[key];
    return val !== undefined && val !== "" && Number.isFinite(Number(val));
  });
  if (!anyFilled) return "";
  return CREDIT_FIELDS.reduce((acc, key) => acc + (Number(data[key]) || 0), 0);
}

function normalizeOfferData(offer = {}, fallback = {}) {
  const src = offer || {};
  const fb = fallback || {};
  const next = {};

  const resolveValue = (key) => {
    const primary = src[key];
    if (primary !== undefined && primary !== null && primary !== "") return primary;
    const secondary = fb[key];
    if (secondary !== undefined && secondary !== null && secondary !== "") return secondary;
    return "";
  };

  const resolveWithAliases = (key) => {
    const value = resolveValue(key);
    if (value !== "") return value;
    const aliases = FIELD_ALIASES[key];
    if (!aliases) return "";
    for (const alias of aliases) {
      const aliasValue = resolveValue(alias);
      if (aliasValue !== "") return aliasValue;
    }
    return "";
  };

  const resolvedOfferId = resolveValue("offerId") || fb.offerId || "";
  next.offerId = resolvedOfferId;
  if (resolvedOfferId) {
    const decoded = decodeOfferId(resolvedOfferId);
    if (decoded.projectId && !src.project_id && !fb.project_id) {
      next.project_id = decoded.projectId;
    }
    if (decoded.contractUnitNumber && !src.contract_unit_number && !fb.contract_unit_number) {
      next.contract_unit_number = decoded.contractUnitNumber;
    }
  }

  next.project_id = next.project_id || resolveValue("project_id") || "Fusion";
  next.contract_unit_number =
    next.contract_unit_number || resolveValue("contract_unit_number") || resolveValue("unit_number") || "";
  next.unit_number = resolveValue("unit_number") || next.contract_unit_number || "";
  next.unit_name = resolveValue("unit_name") || fb.unit_name || "";

  let buyerCombined = resolveValue("buyers_combined") || resolveValue("buyer_name");
  if (!buyerCombined && (resolveValue("buyer_1__full_name") || resolveValue("buyer_2_full_name"))) {
    const b1 = resolveValue("buyer_1__full_name") || "";
    const b2 = resolveValue("buyer_2_full_name") || "";
    buyerCombined = [b1, b2].filter(Boolean).join(" and ");
  }
  next.buyer_name = buyerCombined;
  next.buyers_combined = buyerCombined;

  const textFields = [
    "buyer_1__full_name",
    "buyer_2_full_name",
    "buyer1_email",
    "buyer_1_email",
    "buyer_2_email",
    "buyer_mobile_phone",
    "address_1",
    "address_2",
    "city",
    "state",
    "zip_code",
    "primary_lender",
    "primary_loan_officer_full_name",
    "primary_loan_officer",
    "agent_brokerage",
    "referring_agent_full_name",
    "referring_agent_email",
    "purchase_type",
  ];

  textFields.forEach((field) => {
    const val = resolveValue(field);
    if (val !== "") next[field] = val;
  });

  if (next.buyer_1_email && !next.buyer1_email) next.buyer1_email = next.buyer_1_email;
  if (next.primary_loan_officer && !next.primary_loan_officer_full_name) {
    next.primary_loan_officer_full_name = next.primary_loan_officer;
  }

  const cashVal = resolveValue("cash");
  if (cashVal === "true" || cashVal === true) next.cash_purchase = true;

  const invVal = resolveValue("investor_owner");
  if (invVal === "true" || invVal === true) next.purchase_type = "investment_property";

  const statusVal = resolveValue("status");
  if (statusVal !== "") next.status = statusVal;
  const envelopeVal = resolveValue("docusign_envelope");
  if (envelopeVal !== "") next.docusign_envelope = envelopeVal;
  const notesVal = resolveValue("notes");
  if (notesVal !== "") next.notes = notesVal;

  DATE_FIELDS.forEach((field) => {
    const formatted = formatDateForInput(resolveWithAliases(field));
    if (formatted) {
      next[field] = formatted;
    }
  });

  NUMERIC_FIELDS.forEach((field) => {
    if (field === "total_credits") return;
    const numeric = normalizeNumeric(resolveValue(field));
    if (numeric !== "") {
      next[field] = numeric;
    }
  });

  const credits = calculateTotalCredits({ ...fb, ...src, ...next });
  if (credits !== "") {
    next.total_credits = credits;
  }

  return next;
}

export default function UnifiedForm() {
  const auth = useAuth();
  const jwt = auth?.user?.idToken || auth?.user?.accessToken || null;

  const [form, setForm] = useState({ project_id: "Fusion" });
  const [allUnits, setAllUnits] = useState([]);
  const [projectUnits, setProjectUnits] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const [buildingInfo, setBuildingInfo] = useState("");
  const [planInfo, setPlanInfo] = useState("");
  const [addressInfo, setAddressInfo] = useState("");
  const pdfBtnRef = useRef(null);

  const projectOptions = useMemo(() => {
    const names = Array.from(new Set(allUnits.map((unit) => String(unit.project_id || "").trim()).filter(Boolean)));
    return names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [allUnits]);

  const unitOptions = useMemo(() => {
    return [...projectUnits]
      .filter((unit) => unit?.unit_number !== undefined && unit?.unit_number !== null && unit?.unit_number !== "")
      .sort((a, b) =>
        String(a.unit_number).localeCompare(String(b.unit_number), undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );
  }, [projectUnits]);

  const fetchUnitsForProject = async (projectIdToLook) => {
    if (!projectIdToLook) return [];
    const data = await listUnitsByProject({ project_id: projectIdToLook });
    return Array.isArray(data?.items) ? data.items : [];
  };

  useEffect(() => {
    let cancelled = false;
    async function loadAllUnits() {
      try {
        const response = await listAllUnits();
        if (cancelled) return;
        const items = Array.isArray(response?.items) ? response.items : [];
        setAllUnits(items);
        if (!form.project_id && items.length) {
          const firstProject = String(items[0].project_id || "").trim();
          if (firstProject) {
            setForm((prev) => ({ ...prev, project_id: firstProject }));
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("Failed to load all units for sales form", err);
          setAllUnits([]);
        }
      }
    }
    loadAllUnits();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadProjectUnits() {
      if (!form.project_id) {
        setProjectUnits([]);
        return;
      }
      try {
        const items = await fetchUnitsForProject(form.project_id);
        if (!cancelled) {
          setProjectUnits(items);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn(`Failed to load units for project ${form.project_id}`, err);
          setProjectUnits([]);
        }
      }
    }
    loadProjectUnits();
    return () => {
      cancelled = true;
    };
  }, [form.project_id]);

  useEffect(() => {
    if (!form.project_id || !form.unit_number) return;
    const selectedUnit = unitOptions.find(
      (unit) => String(unit.unit_number).replace(/\.0$/, "") === String(form.unit_number).replace(/\.0$/, "")
    );
    if (selectedUnit) {
      applyHomeDetails(selectedUnit);
    }
  }, [form.project_id, form.unit_number, unitOptions]);

  const handleSearch = async (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    setIsLoading(true);
    setError("");
    setMsg("");
    try {
      if (!jwt) throw new Error("No JWT token available");
      const results = await searchOffers(jwt, q);
      setSearchResults(results);
    } catch (err) {
      console.error("Search error:", err);
      setError(err.message || "Search failed");
    } finally {
      setIsLoading(false);
    }
  };

  const selectResult = (item) => {
    hydrateSelection(item);
  };

  const hydrateSelection = async (item) => {
    setSearchResults([]);
    setError("");
    setMsg("");
    const base = normalizeOfferData({}, item);
    setForm(base);

    try {
      if (!jwt) throw new Error("No JWT token available");
      const record = await getOfferRead(jwt, item.offerId);
      const hydrated = normalizeOfferData(record, item);

      
      setForm(hydrated);

      if (hydrated.project_id && hydrated.contract_unit_number) {
        lookupUnitDetails(hydrated.project_id, hydrated.contract_unit_number);
      }
    } catch (err) {
      console.error("Failed to load offer record:", err);
      setError(err.message || "Failed to load offer details");
      setForm(normalizeOfferData({}, item));
    }
  };

  const lookupUnitDetails = async (projectIdToLook, unitToLook) => {
    const n = String(unitToLook || "").trim();
    if (!n) return;
    try {
      const items = await fetchUnitsForProject(projectIdToLook);
      const checkN = String(n).trim().replace(/\.0$/, "");
      const found = items.find((u) => String(u.unit_number).replace(/\.0$/, "") === checkN);
      if (found) {
        applyHomeDetails(found);
      }
    } catch (e) {
      console.warn("Could not lookup unit detail silently");
    }
  };

  async function handleSelectUnit() {
    setMsg("");
    const n = String(form.unit_number || "").trim();
    if (!n) return setMsg("Enter a unit number.");

    const proj = form.project_id || "Fusion";

    try {
      const items = await fetchUnitsForProject(proj);
      const checkN = String(n).trim().replace(/\.0$/, "");
      const found = items.find((u) => String(u.unit_number).replace(/\.0$/, "") === checkN);

      if (found) {
        applyHomeDetails(found);
      } else {
        setMsg(`Unit ${n} not found in project ${proj}.`);
      }
    } catch (e) {
      setMsg(`Error fetching unit: ${String(e.message || e)}`);
    }
  }

  function applyHomeDetails(obj = {}) {
    const b =
      obj.building_info ||
      (obj.building_id ? `Building ${obj.building_id}, Unit ${obj.unit_number ?? ""}` : "") ||
      "";
    const p = obj.plan_info || obj.plan_type || "";
    const a = obj.address || "";

    setBuildingInfo(b);
    setPlanInfo(p);
    setAddressInfo(a);

    const pdf = obj.pdf_url || obj.floorplan_url || "";
    if (pdfBtnRef.current) {
      if (pdf) {
        pdfBtnRef.current.disabled = false;
        pdfBtnRef.current.onclick = () => window.open(pdf, "_blank");
      } else {
        pdfBtnRef.current.disabled = true;
        pdfBtnRef.current.onclick = null;
      }
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      const next = { ...prev };
      if (type === "checkbox") {
        next[name] = checked;
      } else if (NUMERIC_FIELDS_SET.has(name)) {
        const num = value === "" ? "" : Number(value);
        next[name] = Number.isFinite(num) ? num : "";
      } else {
        next[name] = value;
      }

      if (name === "status") {
        next.statusnumeric = statusNumericFor(value);
        applyStatusDerivedDates(next, value);
      }
      if (name === "project_id") {
        next.unit_number = "";
        next.contract_unit_number = "";
        setBuildingInfo("");
        setPlanInfo("");
        setAddressInfo("");
      }
      if (name === "unit_number") {
        next.contract_unit_number = value;
      }
      if (name === "week_ratified_date") {
        next.fully_executed_date = value;
      } else if (name === "fully_executed_date") {
        next.week_ratified_date = value;
      }
      next.total_credits = calculateTotalCredits(next);
      return next;
    });
  };

  const handlePhoneBlur = (event) => {
    const formatted = formatPhone(event.target.value);
    setForm((prev) => ({ ...prev, [event.target.name]: formatted }));
  };

  function buildBuyersCombined(buyer1, buyer2) {
    const names = [buyer1, buyer2].filter(Boolean);
    if (!names.length) return "";
    if (names.length === 1) return names[0];
    return `${names[0]} and ${names[1]}`;
  }

  const saveTracking = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setMsg("");

    try {
      if (!jwt) throw new Error("No JWT token available");
      const unitRaw = String(form.contract_unit_number || form.unit_number || "").trim();
      const proj = form.project_id || "Fusion";

      if (!unitRaw) {
        throw new Error("Unit number required before saving.");
      }

      let formattedUnit = unitRaw;
      if (/^\d+$/.test(formattedUnit)) {
        formattedUnit = `${formattedUnit}.0`;
      }

      const buyer1 = String(form.buyer_1__full_name || "").trim();
      const buyer2 = String(form.buyer_2_full_name || "").trim();
      const buyersCombined =
        buildBuyersCombined(buyer1, buyer2) || form.buyers_combined || form.buyer_name;

      const payload = {
        ...form,
        contract_unit_number: formattedUnit,
        unit_number: formattedUnit,
        project_id: proj,
        buyers_combined: buyersCombined,
        buyer_current_address: form.address_1 || "",
        buyer_current_city: form.city || "",
        buyer_current_state: form.state || "",
        buyer_current_zip: form.zip_code || "",
        cash: form.cash_purchase ? "true" : "",
        investor_owner: form.purchase_type === "investment_property" ? "true" : "",
      };

      if ((!payload.project_id || !payload.contract_unit_number) && payload.offerId) {
        const decoded = decodeOfferId(payload.offerId);
        if (decoded.projectId && !payload.project_id) payload.project_id = decoded.projectId;
        if (decoded.contractUnitNumber && !payload.contract_unit_number) {
          payload.contract_unit_number = decoded.contractUnitNumber;
        }
      }
      if (payload.status) {
        payload.statusnumeric = statusNumericFor(payload.status);
        applyStatusDerivedDates(payload, payload.status);
        if (payload.status === "closed" && payload.coe_date) {
          payload.status_date = payload.coe_date;
        } else if (
          payload.status === "ratified" &&
          (payload.week_ratified_date || payload.fully_executed_date)
        ) {
          payload.status_date = payload.week_ratified_date || payload.fully_executed_date;
        } else if (!payload.status_date) {
          payload.status_date = new Date().toISOString();
        }
      }
      if (payload.week_ratified_date && !payload.fully_executed_date) {
        payload.fully_executed_date = payload.week_ratified_date;
      } else if (payload.fully_executed_date && !payload.week_ratified_date) {
        payload.week_ratified_date = payload.fully_executed_date;
      }

      let resMsg = "";
      if (payload.offerId) {
        await saveOfferTracking(jwt, payload);
        resMsg = "Tracking saved.";
      } else {
        const saveRes = await createOffer(jwt, payload);
        const newOfferId = saveRes?.offerId || saveRes?.offer_id;
        if (newOfferId) {
          setForm((prev) => ({ ...prev, offerId: newOfferId }));
        }
        resMsg = "Offer created and tracking saved.";
      }

      setMsg(resMsg);
    } catch (e) {
      setMsg(`Save error: ${e.message || "Save failed"}`);
    }
  };

  const formatCurrency = (val) => {
    if (val === undefined || val === "") return "";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(val);
  };

  return (
    <div id="unified-form" style={styles.container}>
      <SalesAppHeader title="Unified Offer & Tracking Form" projectId={form.project_id} />

      <form
        onSubmit={handleSearch}
        style={{
          marginBottom: "1rem",
          display: "flex",
          gap: "0.5rem",
          flexWrap: "wrap",
          padding: "16px",
          background: "#fff",
          border: "1px solid #ddd",
          borderRadius: 8,
        }}
      >
        <input
          type="text"
          placeholder="Lookup by buyer or unit..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ flex: "1 1 240px" }}
        />
        <button type="submit">Lookup</button>
        <button
          type="button"
          onClick={() => {
            setForm({ project_id: "Fusion" });
            setSearchResults([]);
            setMsg("");
          }}
        >
          Clear New
        </button>
        <button type="button" onClick={saveTracking}>
          {form.offerId ? "Save Tracking Update" : "Save New Offer"}
        </button>
      </form>

      {isLoading && <p>Searching…</p>}
      {error && <p style={{ color: "red", padding: "0 16px" }}>{error}</p>}

      {searchResults.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            padding: "0 16px",
            marginBottom: "1rem",
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: 8,
          }}
        >
          {searchResults.map((item) => (
            <li
              key={item.offerId}
              style={{ cursor: "pointer", padding: "0.75rem", borderBottom: "1px solid #eee" }}
              onClick={() => selectResult(item)}
            >
              <strong>{item.project_id || "?"}</strong> /{" "}
              {item.contract_unit_number || item.unit_number || "?"} -{" "}
              {item.buyer_name || "Unknown"} - {(item.status || "").toLowerCase()}
            </li>
          ))}
        </ul>
      )}

      {msg && (
        <div
          style={{
            marginBottom: 10,
            padding: 12,
            background:
              msg.toLowerCase().includes("error") || msg.toLowerCase().includes("failed")
                ? "#ffebee"
                : "#e8f5e9",
            color:
              msg.toLowerCase().includes("error") || msg.toLowerCase().includes("failed")
                ? "crimson"
                : "green",
            borderRadius: 6,
          }}
        >
          {msg}
        </div>
      )}

      <form onSubmit={saveTracking} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <section style={styles.section}>
          <h3 style={styles.legend}>Offer Basics & Buyer Input</h3>

          <div style={{ ...styles.row, gridTemplateColumns: "100px 90px 1.5fr 1fr 1fr 110px auto" }}>
            <label style={styles.col}>
              Project
              <select
                name="project_id"
                value={form.project_id || "Fusion"}
                onChange={handleChange}
                style={styles.input}
              >
                {projectOptions.map((projectId) => (
                  <option key={projectId} value={projectId}>
                    {projectId}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.col}>
              Unit # *
              <select
                name="unit_number"
                value={form.unit_number || ""}
                onChange={handleChange}
                style={styles.input}
              >
                <option value="">Select Unit</option>
                {unitOptions.map((unit) => {
                  const value = String(unit.unit_number);
                  return (
                    <option key={`${unit.project_id}-${value}`} value={value}>
                      {value}
                    </option>
                  );
                })}
              </select>
            </label>

            <label style={styles.col}>
              Buyer (Combined)
              <input
                name="buyers_combined"
                value={form.buyers_combined || ""}
                onChange={handleChange}
                maxLength={100}
                style={styles.input}
              />
            </label>

            <label style={styles.col}>
              Buyer 1 Name
              <input
                name="buyer_1__full_name"
                value={form.buyer_1__full_name || ""}
                onChange={handleChange}
                maxLength={50}
                style={styles.input}
              />
            </label>

            <label style={styles.col}>
              Buyer 2 Name
              <input
                name="buyer_2_full_name"
                value={form.buyer_2_full_name || ""}
                onChange={handleChange}
                maxLength={50}
                style={styles.input}
              />
            </label>

            <label style={styles.col}>
              Status
              <select name="status" value={form.status || "pending"} onChange={handleChange} style={styles.input}>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="contract_sent">Contract Sent</option>
                <option value="ratified">Ratified</option>
                <option value="closed">Closed</option>
                <option value="canceled">Canceled</option>
              </select>
            </label>

            <div style={{ display: "flex", alignItems: "end", gap: 8 }}>
              <button type="button" onClick={handleSelectUnit}>
                Pull Home Details
              </button>
            </div>
          </div>

          <div style={{ ...styles.row, gridTemplateColumns: "1fr", marginTop: 12 }}>
            <label style={styles.col}>
              Current Street Address
              <input name="address_1" value={form.address_1 || ""} onChange={handleChange} style={styles.input} />
            </label>
          </div>

          <div style={{ ...styles.row, gridTemplateColumns: "1fr 90px 140px", marginTop: 12 }}>
            <label style={styles.col}>
              City
              <input name="city" value={form.city || ""} onChange={handleChange} maxLength={55} style={styles.input} />
            </label>
            <label style={styles.col}>
              State
              <input name="state" value={form.state || ""} onChange={handleChange} maxLength={2} style={styles.input} />
            </label>
            <label style={styles.col}>
              Zip Code
              <input name="zip_code" value={form.zip_code || ""} onChange={handleChange} maxLength={15} style={styles.input} />
            </label>
          </div>

          <div style={{ ...styles.row, gridTemplateColumns: "1fr 1fr 1fr", marginTop: 12 }}>
            <label style={styles.col}>
              Mobile Phone
              <input
                name="buyer_mobile_phone"
                value={form.buyer_mobile_phone || ""}
                onChange={handleChange}
                onBlur={handlePhoneBlur}
                maxLength={20}
                style={styles.input}
              />
            </label>
            <label style={styles.col}>
              Buyer 1 Email
              <input
                type="email"
                name="buyer1_email"
                value={form.buyer1_email || ""}
                onChange={handleChange}
                maxLength={40}
                style={styles.input}
              />
            </label>
            <label style={styles.col}>
              Buyer 2 Email
              <input
                type="email"
                name="buyer_2_email"
                value={form.buyer_2_email || ""}
                onChange={handleChange}
                maxLength={40}
                style={styles.input}
              />
            </label>
          </div>
        </section>

        <section style={styles.section}>
          <h3 style={styles.legend}>Home Architecture Details</h3>
          <div style={{ ...styles.row, gridTemplateColumns: "1fr 1fr 1fr auto" }}>
            <label style={styles.col}>Building Info<input value={buildingInfo} readOnly style={styles.inputRO} /></label>
            <label style={styles.col}>Plan Info<input value={planInfo} readOnly style={styles.inputRO} /></label>
            <label style={styles.col}>Address<input value={addressInfo} readOnly style={styles.inputRO} /></label>
            <div style={{ display: "flex", alignItems: "end" }}>
              <button type="button" ref={pdfBtnRef} disabled>
                Open Floorplan
              </button>
            </div>
          </div>
        </section>

        <section style={styles.section}>
          <h3 style={styles.legend}>Agency & Finance Sources</h3>
          <div style={{ ...styles.row, gridTemplateColumns: "1fr 1fr 1fr 1fr" }}>
            <label style={styles.col}>
              Lender Institution
              <input name="primary_lender" value={form.primary_lender || ""} onChange={handleChange} style={styles.input} />
            </label>
            <label style={styles.col}>
              Loan Officer
              <input
                name="primary_loan_officer_full_name"
                value={form.primary_loan_officer_full_name || ""}
                onChange={handleChange}
                style={styles.input}
              />
            </label>
            <label style={styles.col}>
              Referring Agent Name
              <input
                name="referring_agent_full_name"
                value={form.referring_agent_full_name || ""}
                onChange={handleChange}
                style={styles.input}
              />
            </label>
            <label style={styles.col}>
              Agent Brokerage
              <input
                name="agent_brokerage"
                value={form.agent_brokerage || ""}
                onChange={handleChange}
                style={styles.input}
              />
            </label>
          </div>

          <div style={{ ...styles.row, gridTemplateColumns: "auto 1fr 1fr", marginTop: 12 }}>
            <label style={{ ...styles.col, alignItems: "center", flexDirection: "row", gap: 8 }}>
              <input type="checkbox" name="cash_purchase" checked={form.cash_purchase || false} onChange={handleChange} />
              Cash Purchase?
            </label>
            <label style={styles.col}>
              Base Price ($) (Deprecated - Use Financials)
              <input
                type="number"
                name="base_price"
                value={form.base_price ?? ""}
                onChange={handleChange}
                step="1"
                min="0"
                inputMode="numeric"
                style={styles.input}
              />
              {form.base_price && <small style={{ color: "#555" }}>{formatCurrency(form.base_price)}</small>}
            </label>

            <label style={styles.col}>
              Purchase Type
              <select name="purchase_type" value={form.purchase_type || ""} onChange={handleChange} style={styles.input}>
                <option value="">Select...</option>
                <option value="personal_residence">Personal Residence</option>
                <option value="investment_property">Investment Property</option>
              </select>
            </label>
          </div>
        </section>
        <section style={styles.section}>
          <h3 style={styles.legend}>Key Dates</h3>
          <div style={{ ...styles.row, gridTemplateColumns: "repeat(3, 1fr)" }}>
            {DATE_FIELDS_UI.map((field) => (
              <label key={field} style={styles.col}>
                {DATE_FIELD_LABELS[field] || field.replace(/_/g, " ")}
                <input
                  type="date"
                  name={field}
                  value={form[field] || ""}
                  onChange={handleChange}
                  style={styles.input}
                />
              </label>
            ))}
          </div>
        </section>

        <section style={styles.section}>
          <h3 style={styles.legend}>Financials</h3>
          <div style={{ ...styles.row, gridTemplateColumns: "repeat(3, 1fr)" }}>
            {[
              "final_price",
              "list_price",
              "initial_deposit_amount",
              "seller_credit",
              "upgrade_credit",
              "total_upgrades_solar",
              "hoa_credit",
              "total_credits",
            ].map((field) => (
              <label key={field} style={styles.col}>
                {field.replace(/_/g, " ")} ($)
                <input
                  type="number"
                  name={field}
                  value={form[field] ?? ""}
                  onChange={handleChange}
                  step="1"
                  min="0"
                  inputMode="numeric"
                  style={styles.input}
                />
                {form[field] && <small style={{ color: "#555" }}>{formatCurrency(form[field])}</small>}
              </label>
            ))}
          </div>
        </section>

        <section style={styles.section}>
          <h3 style={styles.legend}>DocuSign & Tracking Notes</h3>
          <div style={{ ...styles.row, gridTemplateColumns: "repeat(3, 1fr)" }}>
            <label style={{ ...styles.col, gridColumn: "1 / -1" }}>
              DocuSign Envelope
              <input
                type="text"
                name="docusign_envelope"
                value={form.docusign_envelope || ""}
                onChange={handleChange}
                style={styles.input}
              />
            </label>
            <label style={{ ...styles.col, gridColumn: "1 / -1" }}>
              Notes
              <textarea
                name="notes"
                value={form.notes || ""}
                onChange={handleChange}
                rows={4}
                style={styles.textarea}
              />
            </label>
          </div>
        </section>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="submit">{form.offerId ? "Save Tracking Update" : "Save New Offer"}</button>
        </div>
      </form>
    </div>
  );
}

const styles = {
  container: { maxWidth: 1100, margin: "0 auto", padding: 16, fontFamily: "Lato, Arial, sans-serif" },
  section: { background: "#fff", border: "1px solid #ddd", borderRadius: 8, padding: 16 },
  legend: { margin: 0, marginBottom: 12, fontWeight: 700, fontSize: "1.05rem", color: "#333" },
  row: { display: "grid", gap: 12, alignItems: "end" },
  col: { display: "flex", flexDirection: "column", minWidth: 0 },
  input: { padding: "10px", border: "1px solid #ccc", borderRadius: 6, fontSize: "1rem" },
  inputRO: { padding: "10px", border: "1px solid #eee", borderRadius: 6, fontSize: "1rem", background: "#f5f5f5", color: "#555" },
  textarea: { padding: "10px", border: "1px solid #ccc", borderRadius: 6, fontSize: "1rem", resize: "vertical" },
};




